import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Rocket } from "lucide-react";

const PLAN_ICON = { starter: Rocket, growth: Sparkles, pro: Crown };
const FEATURE_LABEL = {
  overview: "Sales Overview", orders: "Order Details", guide: "Reports Guide",
  cost: "Cost & Profit", shipments: "FBA Shipments", tx: "Transactions (US/EU)",
  warehouse: "Warehouse", po: "Purchase Orders",
};

export default function BillingTab() {
  const { plan: currentPlan } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const checkout = async (p) => {
    const lookup = period === "yearly" ? p.lookup_yearly : p.lookup_monthly;
    if (!lookup) return;
    setBusy(p.id);
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error("Could not start checkout. Please try again."); setBusy(""); }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-8" data-testid="billing-tab">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="font-head font-extrabold text-3xl text-white">Choose your plan</h2>
        <p className="text-sm text-[#A3A3A3] mt-2">Unlock deeper analytics as you scale. Cancel anytime.</p>
        <div className="inline-flex items-center gap-1 mt-5 p-1 rounded-full bg-[#111] border border-[#262626]" data-testid="billing-period-toggle">
          {["monthly", "yearly"].map((pr) => (
            <button key={pr} data-testid={`period-${pr}`} onClick={() => setPeriod(pr)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${period === pr ? "bg-[#007AFF] text-white" : "text-[#A3A3A3] hover:text-white"}`}>
              {pr === "monthly" ? "Monthly" : "Annual"}{pr === "yearly" && <span className="ml-1 text-[11px] text-[#10B981]">save ~17%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {data.plans.map((p) => {
          const Icon = PLAN_ICON[p.id] || Rocket;
          const price = period === "yearly" ? p.price_yearly : p.price_monthly;
          const isCurrent = currentPlan === p.id;
          const highlight = p.id === "pro";
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl border p-6 flex flex-col ${highlight ? "border-[#007AFF] bg-[#007AFF]/[0.06]" : "border-white/10 bg-[#0f0f0f]"}`}>
              {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#007AFF] text-white text-xs font-bold">Most popular</span>}
              <div className="flex items-center gap-2">
                <Icon className={`h-5 w-5 ${highlight ? "text-[#007AFF]" : "text-[#A3A3A3]"}`} />
                <h3 className="font-head font-bold text-white text-lg">{p.name}</h3>
              </div>
              <div className="mt-4 flex items-end gap-1">
                <span className="text-4xl font-extrabold text-white tnum">${price}</span>
                <span className="text-sm text-[#666] mb-1">/{period === "yearly" ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#D4D4D4]">
                    <Check className="h-4 w-4 text-[#10B981] shrink-0" /> {FEATURE_LABEL[f] || f}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <Button disabled data-testid={`plan-current-${p.id}`} className="w-full bg-[#1a1a1a] text-[#A3A3A3] rounded-full cursor-default">Current plan</Button>
                ) : p.id === "starter" ? (
                  <Button disabled className="w-full bg-[#1a1a1a] text-[#666] rounded-full cursor-default">Free tier</Button>
                ) : (
                  <Button data-testid={`plan-choose-${p.id}`} disabled={busy === p.id} onClick={() => checkout(p)}
                    className={`w-full rounded-full font-semibold ${highlight ? "bg-[#007AFF] hover:bg-[#005BB5]" : "bg-white text-black hover:bg-[#e5e5e5]"}`}>
                    {busy === p.id ? "Redirecting…" : `Upgrade to ${p.name}`}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-[#555]">Test mode · use card 4242 4242 4242 4242, any future expiry & CVC.</p>
    </div>
  );
}
